import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogPosts } from '../data/blogData';
import { FiArrowLeft, FiCalendar, FiUser, FiTag } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';

const BlogPost = () => {
  const { id } = useParams();
  const post = blogPosts.find(p => p.id === id);

  if (!post) {
    return (
      <div className="pt-40 pb-20 text-center">
        <h1 className="text-4xl mb-4">Article Not Found</h1>
        <Link to="/blog" className="text-brand-500 hover:underline">Return to Blog</Link>
      </div>
    );
  }

  // Helper to render content with basic formatting
  const renderContent = (content) => {
    return content.split('\n\n').map((paragraph, idx) => {
      if (paragraph.startsWith('### ')) {
        return <h3 key={idx} className="text-2xl font-serif text-brand-800 mt-8 mb-4">{paragraph.replace('### ', '')}</h3>;
      }
      if (paragraph.startsWith('* ')) {
        const items = paragraph.split('\n').map(item => item.replace('* ', ''));
        return (
          <ul key={idx} className="list-disc list-inside text-brand-700 mb-6 space-y-2 ml-4">
            {items.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ 
                __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
              }} />
            ))}
          </ul>
        );
      }
      return (
        <p 
          key={idx} 
          className="text-brand-700 mb-6 leading-relaxed text-lg"
          dangerouslySetInnerHTML={{ 
            __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') 
          }} 
        />
      );
    });
  };

  return (
    <div className="page-transition">
      {/* Hero Header with Video Background */}
      <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <video 
          className="w-full h-full object-cover absolute inset-0" 
          autoPlay 
          loop 
          muted 
          playsInline 
          src="https://canv.wasacon.com/amani-resort-zanzibar/_assets/video/eae82d0564857a215475c13dfd6b0941.mp4" 
        />
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-black/50 flex flex-col justify-end pb-20">
          <div className="container mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors text-sm uppercase tracking-widest font-bold">
              <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Journal
            </Link>
            <div className="max-w-4xl">
              <span className="inline-block px-3 py-1 bg-brand-500 text-white text-xs font-bold uppercase tracking-widest rounded-sm mb-4">
                {post.category}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-serif leading-tight mb-6">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-white/90 text-sm font-medium">
                <span className="flex items-center gap-2">
                  <SafeIcon icon={FiCalendar} /> {post.date}
                </span>
                <span className="flex items-center gap-2">
                  <SafeIcon icon={FiUser} /> {post.author}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg prose-stone max-w-none">
              {renderContent(post.content)}
            </div>
            
            <hr className="my-12 border-brand-100" />
            
            <div className="flex flex-col md:flex-row justify-between items-center bg-brand-50 p-8 rounded-xl gap-6">
              <div className="text-center md:text-left">
                <h3 className="text-xl font-serif text-brand-900 mb-2">Experience it yourself</h3>
                <p className="text-brand-600 text-sm">Book your Halal-friendly stay at Amani Palms today.</p>
              </div>
              <Button to="/contact" variant="primary">Book Your Stay</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;